import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, ChevronDown } from "lucide-react";
import BaseSelect from "../../Base/BaseSelect";
import EmailSenderQuickAddModal, {
  EmailSenderQuickCreated,
} from "../../Companies/EmailSenderQuickAddModal";
import showToast from "../../../utils/toast";
import {
  ServiceEmailRequest,
  ServiceEmailRequestSubmitData,
  ServiceEmailSenderOption,
  ServiceEmailSolicitudDraft,
  ServiceEmailStatus,
} from "../../../models/ServiceEmailRequestModel";
import {
  MULTI_DAY_JSON_PLACEHOLDER,
  parseOparaEmailJson,
} from "../../../utils/parseOparaEmailJson";
import useServiceEmailsService from "../../../services/useServiceEmailsService";

type Props = {
  isOpen: boolean;
  item?: ServiceEmailRequest | null;
  companies: Array<{ value: string; label: string }>;
  companyFilter: string;
  senderOptions: ServiceEmailSenderOption[];
  loadingSenders?: boolean;
  loading?: boolean;
  onClose: () => void;
  onCompanyFilterChange: (companyId: string) => void;
  onSubmit: (
    data: ServiceEmailRequestSubmitData | ServiceEmailRequestSubmitData[],
    item?: ServiceEmailRequest | null
  ) => void | Promise<void>;
};

type InputMode = "form" | "json";
type WizardStep = "edit" | "verify-senders";

type EmailFormState = {
  subject: string;
  received_at: string;
  sender_ref: string;
  has_multiple_requests: boolean;
  status: ServiceEmailStatus;
};

type SolicitudRow = {
  key: string;
  passenger_count: string;
  passenger_names: string;
  request_date: string;
  request_time: string;
  origin_text: string;
  destination_text: string;
};

type VerifyDraft = {
  key: string;
  company_id: string;
  sender_ref: string;
  data: ServiceEmailRequestSubmitData;
};

const JSON_PLACEHOLDER = `{
  "email": {
    "destination_text": "Hotel Aguas del Desierto",
    "has_multiple_requests": false,
    "origin_text": "Planta Río Loa",
    "passenger_count": 1,
    "received_at": "2026-07-27T10:59:00",
    "request_date": "2026-07-27",
    "request_time": "12:30:00",
    "status": "PENDIENTE",
    "subject": "Aviso al proveedor (170480)"
  },
  "solicitudes": [
    {
      "destination_text": "Hotel Aguas del Desierto",
      "origin_text": "Planta Río Loa",
      "passenger_count": 1,
      "passenger_names": "NOMBRE, APELLIDO",
      "request_date": "2026-07-27",
      "request_time": "12:30:00"
    }
  ]
}`;

const emptyEmailForm = (): EmailFormState => ({
  subject: "",
  received_at: "",
  sender_ref: "",
  has_multiple_requests: false,
  status: "PENDIENTE",
});

const createSolicitudRow = (): SolicitudRow => ({
  key: `${Date.now()}-${Math.random()}`,
  passenger_count: "1",
  passenger_names: "",
  request_date: "",
  request_time: "",
  origin_text: "",
  destination_text: "",
});

const copyTripFields = (
  source: SolicitudRow
): Pick<SolicitudRow, "request_date" | "request_time" | "origin_text" | "destination_text"> => ({
  request_date: source.request_date,
  request_time: source.request_time,
  origin_text: source.origin_text,
  destination_text: source.destination_text,
});

const createSolicitudRowFrom = (source?: SolicitudRow): SolicitudRow => ({
  ...createSolicitudRow(),
  ...(source ? copyTripFields(source) : {}),
});

const toDateTimeInputValue = (raw?: string | null) => {
  if (!raw) return "";
  return String(raw).slice(0, 16);
};

const getSenderRef = (item?: ServiceEmailRequest | null) => {
  if (!item) return "";
  if (item.sender_type === "AUTHORIZER" && item.authorizer) return `AUTHORIZER:${item.authorizer}`;
  if (item.sender_type === "REQUESTER" && item.requester) return `REQUESTER:${item.requester}`;
  return "";
};

const rowFromEmail = (item: ServiceEmailRequest): SolicitudRow => ({
  key: "base",
  passenger_count: String(item.passenger_count ?? 1),
  passenger_names: "",
  request_date: item.request_date ?? "",
  request_time: item.request_time ? String(item.request_time).slice(0, 5) : "",
  origin_text: item.origin_text ?? "",
  destination_text: item.destination_text ?? "",
});

const formatDay = (raw?: string | null) => {
  if (!raw) return "Sin fecha";
  const [y, m, d] = String(raw).slice(0, 10).split("-");
  if (!y || !m || !d) return String(raw);
  return `${d}/${m}/${y.slice(-2)}`;
};

const ServiceEmailRequestModal: React.FC<Props> = ({
  isOpen,
  item,
  companies,
  companyFilter,
  senderOptions,
  loadingSenders = false,
  loading = false,
  onClose,
  onCompanyFilterChange,
  onSubmit,
}) => {
  const { senderOptions: loadSenderOptions } = useServiceEmailsService();
  const [inputMode, setInputMode] = useState<InputMode>("form");
  const [wizardStep, setWizardStep] = useState<WizardStep>("edit");
  const [form, setForm] = useState<EmailFormState>(emptyEmailForm);
  const [solicitudes, setSolicitudes] = useState<SolicitudRow[]>([createSolicitudRow()]);
  const [jsonText, setJsonText] = useState("");
  const [jsonMultiDay, setJsonMultiDay] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [senderModalOpen, setSenderModalOpen] = useState(false);
  const [senderModalDraftKey, setSenderModalDraftKey] = useState<string | null>(null);
  const [extraSenders, setExtraSenders] = useState<ServiceEmailSenderOption[]>([]);
  const [verifyDrafts, setVerifyDrafts] = useState<VerifyDraft[]>([]);
  const [sendersByCompany, setSendersByCompany] = useState<Record<string, ServiceEmailSenderOption[]>>(
    {}
  );
  const [loadingVerifySenders, setLoadingVerifySenders] = useState(false);
  const [expandedDraftKey, setExpandedDraftKey] = useState<string | null>(null);
  const isEditing = Boolean(item?.id);

  useEffect(() => {
    if (!isOpen) return;
    setSubmitted(false);
    setJsonError(null);
    setInputMode("form");
    setWizardStep("edit");
    setJsonText("");
    setJsonMultiDay(false);
    setSenderModalOpen(false);
    setSenderModalDraftKey(null);
    setExtraSenders([]);
    setVerifyDrafts([]);
    setSendersByCompany({});
    setExpandedDraftKey(null);
    if (!item) {
      setForm(emptyEmailForm());
      setSolicitudes([createSolicitudRow()]);
      return;
    }
    setForm({
      subject: item.subject ?? "",
      received_at: toDateTimeInputValue(item.received_at),
      sender_ref: getSenderRef(item),
      has_multiple_requests: Boolean(item.has_multiple_requests),
      status: item.status ?? "PENDIENTE",
    });
    setSolicitudes([rowFromEmail(item)]);
  }, [isOpen, item]);

  const mergedSenderOptions = useMemo(() => {
    const byValue = new Map<string, ServiceEmailSenderOption>();
    for (const option of senderOptions) byValue.set(option.value, option);
    for (const option of extraSenders) byValue.set(option.value, option);
    return Array.from(byValue.values());
  }, [senderOptions, extraSenders]);

  const senderSelectOptions = useMemo(
    () => mergedSenderOptions.map((option) => ({ value: option.value, label: option.label })),
    [mergedSenderOptions]
  );

  const companySelectOptions = useMemo(
    () => companies.map((company) => ({ value: company.value, label: company.label })),
    [companies]
  );

  const selectedCompanyName = useMemo(
    () => companies.find((company) => company.value === companyFilter)?.label,
    [companies, companyFilter]
  );

  const companyIdNumber = Number(companyFilter) || 0;
  const senderDisabled = !companyFilter || loadingSenders;

  const ensureSendersForCompany = async (companyId: string) => {
    if (!companyId || sendersByCompany[companyId]) return;
    setLoadingVerifySenders(true);
    await loadSenderOptions(
      { company_id: companyId },
      {
        onSuccess: (resp: any) => {
          const options = Array.isArray(resp?.data?.sender_options)
            ? resp.data.sender_options
            : [];
          setSendersByCompany((prev) => ({ ...prev, [companyId]: options }));
          setLoadingVerifySenders(false);
        },
        onError: () => {
          setLoadingVerifySenders(false);
          showToast("No se pudieron cargar emisores", "error");
        },
      }
    );
  };

  const handleCompanyChange = (companyId: string) => {
    setForm((prev) => ({ ...prev, sender_ref: "" }));
    setExtraSenders([]);
    onCompanyFilterChange(companyId);
  };

  const openSenderQuickAdd = (draftKey?: string) => {
    const companyId =
      draftKey != null
        ? verifyDrafts.find((d) => d.key === draftKey)?.company_id || ""
        : companyFilter;
    if (!companyId) {
      showToast("Selecciona una empresa para agregar un emisor.", "error");
      return;
    }
    setSenderModalDraftKey(draftKey ?? null);
    if (draftKey == null) {
      setSenderModalOpen(true);
      return;
    }
    setSenderModalOpen(true);
  };

  const handleSenderCreated = (result: EmailSenderQuickCreated) => {
    setExtraSenders((prev) => {
      const byValue = new Map(prev.map((option) => [option.value, option]));
      for (const option of result.options) byValue.set(option.value, option);
      return Array.from(byValue.values());
    });

    if (senderModalDraftKey) {
      const draft = verifyDrafts.find((d) => d.key === senderModalDraftKey);
      if (draft?.company_id) {
        setSendersByCompany((prev) => {
          const current = prev[draft.company_id] ?? [];
          const byValue = new Map(current.map((option) => [option.value, option]));
          for (const option of result.options) byValue.set(option.value, option);
          return { ...prev, [draft.company_id]: Array.from(byValue.values()) };
        });
      }
      setVerifyDrafts((prev) =>
        prev.map((row) =>
          row.key === senderModalDraftKey ? { ...row, sender_ref: result.sender_ref } : row
        )
      );
    } else {
      setForm((prev) => ({ ...prev, sender_ref: result.sender_ref }));
    }
    setSenderModalOpen(false);
    setSenderModalDraftKey(null);
  };

  const updateField = <K extends keyof EmailFormState>(key: K, value: EmailFormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "has_multiple_requests" && value === false) {
        setSolicitudes((rows) => (rows.length > 0 ? [rows[0]] : [createSolicitudRow()]));
      }
      return next;
    });
  };

  const updateSolicitud = (key: string, patch: Partial<SolicitudRow>) => {
    setSolicitudes((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const addSolicitud = () => {
    setSolicitudes((prev) => [...prev, createSolicitudRowFrom(prev[prev.length - 1])]);
  };

  const removeSolicitud = (key: string) => {
    setSolicitudes((prev) => (prev.length === 1 ? prev : prev.filter((row) => row.key !== key)));
  };

  const repeatTripFromPrevious = (key: string, index: number) => {
    setSolicitudes((prev) => {
      if (index <= 0) return prev;
      const source = prev[index - 1];
      if (!source) return prev;
      return prev.map((row) => (row.key === key ? { ...row, ...copyTripFields(source) } : row));
    });
  };

  const repeatTripFromFirst = () => {
    const source = solicitudes[0];
    if (!source || solicitudes.length < 2) return;
    setSolicitudes((prev) =>
      prev.map((row, index) => (index === 0 ? row : { ...row, ...copyTripFields(source) }))
    );
  };

  const buildSolicitudPayloads = (): ServiceEmailSolicitudDraft[] =>
    solicitudes.map((row) => ({
      passenger_count: Math.max(1, Number(row.passenger_count) || 1),
      passenger_names: row.passenger_names.trim(),
      request_date: row.request_date || null,
      request_time: row.request_time || null,
      origin_text: row.origin_text.trim(),
      destination_text: row.destination_text.trim(),
    }));

  const buildSubmitData = (): ServiceEmailRequestSubmitData => {
    const items = buildSolicitudPayloads();
    const first = items[0] ?? { passenger_count: 1 };
    return {
      email: {
        subject: form.subject.trim(),
        received_at: form.received_at || null,
        sender_ref: form.sender_ref,
        has_multiple_requests: form.has_multiple_requests || items.length > 1,
        request_date: first.request_date ?? null,
        request_time: first.request_time ?? null,
        passenger_count: first.passenger_count,
        origin_text: first.origin_text ?? "",
        destination_text: first.destination_text ?? "",
        status: form.status,
      },
      solicitudes: items,
    };
  };

  const solicitudesValid = solicitudes.every((row) => Number(row.passenger_count) >= 1);

  const enterVerifyStep = async (items: ServiceEmailRequestSubmitData[]) => {
    const drafts: VerifyDraft[] = items.map((data, index) => ({
      key: `verify-${index}-${data.email.request_date || "x"}`,
      company_id: companyFilter || "",
      sender_ref: data.email.sender_ref || form.sender_ref || "",
      data,
    }));
    setVerifyDrafts(drafts);
    setWizardStep("verify-senders");
    const companyIds = Array.from(new Set(drafts.map((d) => d.company_id).filter(Boolean)));
    await Promise.all(companyIds.map((id) => ensureSendersForCompany(id)));
  };

  const handleParseJsonContinue = async () => {
    setSubmitted(true);
    setJsonError(null);
    const result = parseOparaEmailJson(jsonText, form.sender_ref, {
      requireSender: false,
      splitByDay: jsonMultiDay,
    });
    if (!result.ok) {
      setJsonError(result.error);
      return;
    }

    // Multi-día o varios correos en el JSON → paso de verificación de emisores
    const needsVerify = jsonMultiDay || result.items.length > 1;
    if (needsVerify) {
      await enterVerifyStep(result.items);
      return;
    }

    const only = result.items[0];
    if (!only) {
      setJsonError("No hay correos para crear.");
      return;
    }
    const sender_ref = only.email.sender_ref || form.sender_ref;
    if (!sender_ref) {
      setJsonError("Selecciona un emisor (o inclúyelo como sender_ref en el JSON).");
      return;
    }
    await onSubmit({ ...only, email: { ...only.email, sender_ref } }, item);
  };

  const handleVerifySubmit = async () => {
    setSubmitted(true);
    const missing = verifyDrafts.some((draft) => !draft.company_id || !draft.sender_ref);
    if (missing) {
      showToast("Verifica empresa y emisor en todos los correos.", "error");
      return;
    }
    const payload = verifyDrafts.map((draft) => ({
      ...draft.data,
      email: { ...draft.data.email, sender_ref: draft.sender_ref },
    }));
    await onSubmit(payload, item);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (wizardStep === "verify-senders") {
      await handleVerifySubmit();
      return;
    }

    setSubmitted(true);
    setJsonError(null);

    if (inputMode === "json" && !isEditing) {
      await handleParseJsonContinue();
      return;
    }

    if (!form.subject.trim() || !form.sender_ref || !solicitudesValid) return;
    await onSubmit(buildSubmitData(), item);
  };

  if (!isOpen) return null;

  const senderModalCompanyId =
    senderModalDraftKey != null
      ? Number(verifyDrafts.find((d) => d.key === senderModalDraftKey)?.company_id) || 0
      : companyIdNumber;
  const senderModalCompanyName =
    companies.find((c) => c.value === String(senderModalCompanyId))?.label || selectedCompanyName;

  return (
    <div className="services-modal" role="dialog" aria-modal="true" aria-labelledby="service-email-modal-title">
      <form
        className={`services-modal-inner services-modal-inner--wide service-email-modal${
          wizardStep === "verify-senders" ? " service-email-modal--verify" : ""
        }`}
        onSubmit={handleSubmit}
      >
        <header className="services-modal-header">
          <div className="services-modal-title-wrap">
            <h2 id="service-email-modal-title" className="services-modal-title">
              {wizardStep === "verify-senders"
                ? "Verificar emisores"
                : isEditing
                  ? "Editar correo"
                  : "Nuevo correo"}
            </h2>
            <span className="services-modal-step">
              {wizardStep === "verify-senders"
                ? `${verifyDrafts.length} correo${verifyDrafts.length !== 1 ? "s" : ""}`
                : "Pendiente | Agregado"}
            </span>
          </div>
          <p className="services-modal-subtitle">
            {wizardStep === "verify-senders"
              ? "Confirma empresa y emisor de cada correo antes de crearlos."
              : inputMode === "json"
                ? "Pega el JSON OPARA. Activa multi-día para varios días o varios correos y verificar emisores."
                : "Registra el correo y agrega las solicitudes con la información del viaje."}
          </p>
        </header>

        {wizardStep === "verify-senders" ? (
          <div className="services-body services-body--wide service-email-verify-list">
            <div className="service-email-verify-toolbar">
              <button
                type="button"
                className="service-email-verify-toolbar__btn"
                onClick={() => {
                  const sorted = [...verifyDrafts].sort((a, b) => {
                    const dayA = a.data.email.request_date || "";
                    const dayB = b.data.email.request_date || "";
                    return dayA.localeCompare(dayB);
                  });
                  const first = sorted[0];
                  if (!first || !first.company_id || !first.sender_ref) return;
                  setVerifyDrafts((prev) =>
                    prev.map((draft) => ({
                      ...draft,
                      company_id: first.company_id,
                      sender_ref: first.sender_ref,
                    }))
                  );
                }}
                disabled={
                  !verifyDrafts.some((d) => d.company_id && d.sender_ref)
                }
              >
                Aplicar empresa y emisor del primero a todos
              </button>
            </div>
            <div className="service-email-verify-list-items">
              {verifyDrafts.sort((a, b) => {
                const dayA = a.data.email.request_date || "";
                const dayB = b.data.email.request_date || "";
                return dayA.localeCompare(dayB);
              }).map((draft, index) => {
                const day = draft.data.email.request_date;
                const isExpanded = expandedDraftKey === draft.key;
                const options = [
                  ...(sendersByCompany[draft.company_id] ?? []),
                  ...extraSenders,
                ];
                const byValue = new Map(options.map((o) => [o.value, o]));
                const senderOpts = Array.from(byValue.values()).map((o) => ({
                  value: o.value,
                  label: o.label,
                }));
                const solicitud = draft.data.solicitudes[0];

                return (
                  <div
                    key={draft.key}
                    className={`service-email-verify-item ${isExpanded ? "is-expanded" : ""} ${
                      draft.company_id && draft.sender_ref ? "is-valid" : ""
                    } ${
                      submitted && (!draft.company_id || !draft.sender_ref) ? "is-invalid" : ""
                    }`}
                  >
                    <div
                      className="service-email-verify-item-header"
                      onClick={() =>
                        setExpandedDraftKey(isExpanded ? null : draft.key)
                      }
                    >
                      <div className="service-email-verify-item-index">{index + 1}</div>
                      <div className="service-email-verify-item-info">
                        <div className="service-email-verify-item-title">
                          {draft.data.email.subject}
                        </div>
                        <div className="service-email-verify-item-meta">
                          <span className="service-email-verify-item-day">{formatDay(day)}</span>
                          {solicitud && (
                            <>
                              <span className="service-email-verify-item-separator">·</span>
                              <span className="service-email-verify-item-origin">
                                {solicitud.origin_text || "—"}
                              </span>
                              <span className="service-email-verify-item-arrow">→</span>
                              <span className="service-email-verify-item-dest">
                                {solicitud.destination_text || "—"}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        className="service-email-verify-item-expand"
                        aria-expanded={isExpanded}
                      >
                        <ChevronDown
                          size={20}
                          strokeWidth={2}
                          style={{
                            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        />
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="service-email-verify-item-content">
                        <div className="service-email-verify-item-details">
                          {solicitud && (
                            <div className="service-email-verify-details-section">
                              <h4>Solicitud</h4>
                              <div className="service-email-verify-detail-row">
                                <span className="label">Pasajeros:</span>
                                <div className="service-email-verify-edit-field">
                                  <input
                                    type="number"
                                    min="1"
                                    value={solicitud.passenger_count ?? 1}
                                    onChange={(e) =>
                                      setVerifyDrafts((prev) =>
                                        prev.map((row) =>
                                          row.key === draft.key
                                            ? {
                                                ...row,
                                                data: {
                                                  ...row.data,
                                                  solicitudes: [
                                                    {
                                                      ...solicitud,
                                                      passenger_count: Number(e.target.value) || 1,
                                                    } as any,
                                                  ],
                                                },
                                              }
                                            : row
                                        )
                                      )
                                    }
                                  />
                                  <input
                                    type="text"
                                    placeholder="Nombres"
                                    value={solicitud.passenger_names || ""}
                                    onChange={(e) =>
                                      setVerifyDrafts((prev) =>
                                        prev.map((row) =>
                                          row.key === draft.key
                                            ? {
                                                ...row,
                                                data: {
                                                  ...row.data,
                                                  solicitudes: [
                                                    {
                                                      ...solicitud,
                                                      passenger_names: e.target.value,
                                                    } as any,
                                                  ],
                                                },
                                              }
                                            : row
                                        )
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <div className="service-email-verify-detail-row">
                                <span className="label">Origen:</span>
                                <input
                                  type="text"
                                  className="service-email-verify-edit-input"
                                  value={solicitud.origin_text || ""}
                                  onChange={(e) =>
                                    setVerifyDrafts((prev) =>
                                      prev.map((row) =>
                                        row.key === draft.key
                                          ? {
                                              ...row,
                                              data: {
                                                ...row.data,
                                                email: { ...row.data.email, origin_text: e.target.value },
                                                solicitudes: [
                                                  { ...solicitud, origin_text: e.target.value } as any,
                                                ],
                                              },
                                            }
                                          : row
                                      )
                                    )
                                  }
                                />
                              </div>
                              <div className="service-email-verify-detail-row">
                                <span className="label">Destino:</span>
                                <input
                                  type="text"
                                  className="service-email-verify-edit-input"
                                  value={solicitud.destination_text || ""}
                                  onChange={(e) =>
                                    setVerifyDrafts((prev) =>
                                      prev.map((row) =>
                                        row.key === draft.key
                                          ? {
                                              ...row,
                                              data: {
                                                ...row.data,
                                                email: { ...row.data.email, destination_text: e.target.value },
                                                solicitudes: [
                                                  { ...solicitud, destination_text: e.target.value } as any,
                                                ],
                                              },
                                            }
                                          : row
                                      )
                                    )
                                  }
                                />
                              </div>
                              <div className="service-email-verify-detail-row">
                                <span className="label">Fecha:</span>
                                <input
                                  type="date"
                                  className="service-email-verify-edit-input"
                                  value={solicitud.request_date || ""}
                                  onChange={(e) =>
                                    setVerifyDrafts((prev) =>
                                      prev.map((row) =>
                                        row.key === draft.key
                                          ? {
                                              ...row,
                                              data: {
                                                ...row.data,
                                                email: { ...row.data.email, request_date: e.target.value },
                                                solicitudes: [
                                                  { ...solicitud, request_date: e.target.value } as any,
                                                ],
                                              },
                                            }
                                          : row
                                      )
                                    )
                                  }
                                />
                              </div>
                              <div className="service-email-verify-detail-row">
                                <span className="label">Hora:</span>
                                <input
                                  type="time"
                                  className="service-email-verify-edit-input"
                                  value={solicitud.request_time || ""}
                                  onChange={(e) =>
                                    setVerifyDrafts((prev) =>
                                      prev.map((row) =>
                                        row.key === draft.key
                                          ? {
                                              ...row,
                                              data: {
                                                ...row.data,
                                                email: { ...row.data.email, request_time: e.target.value },
                                                solicitudes: [
                                                  { ...solicitud, request_time: e.target.value } as any,
                                                ],
                                              },
                                            }
                                          : row
                                      )
                                    )
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="service-email-verify-item-fields">
                          <div className="service-email-field">
                            <label>Empresa</label>
                            <BaseSelect
                              label=""
                              name={`verify-company-${draft.key}`}
                              value={draft.company_id}
                              placeholder="Selecciona empresa"
                              searchable
                              options={companySelectOptions}
                              onChange={async (event) => {
                                const companyId = event.target.value;
                                setVerifyDrafts((prev) =>
                                  prev.map((row) =>
                                    row.key === draft.key
                                      ? { ...row, company_id: companyId, sender_ref: "" }
                                      : row
                                  )
                                );
                                await ensureSendersForCompany(companyId);
                              }}
                            />
                          </div>

                          <div className="service-email-field">
                            <div className="service-email-field__head">
                              <label>Emisor</label>
                              <button
                                type="button"
                                className="service-email-field__add-btn"
                                onClick={() => openSenderQuickAdd(draft.key)}
                                disabled={!draft.company_id}
                              >
                                <UserPlus size={16} strokeWidth={2.25} aria-hidden />
                                Nuevo
                              </button>
                            </div>
                            <BaseSelect
                              label=""
                              name={`verify-sender-${draft.key}`}
                              value={draft.sender_ref}
                              placeholder={
                                draft.company_id ? "Selecciona emisor" : "Selecciona empresa primero"
                              }
                              searchable
                              preserveCase
                              disabled={!draft.company_id || loadingVerifySenders}
                              options={senderOpts}
                              onChange={(event) =>
                                setVerifyDrafts((prev) =>
                                  prev.map((row) =>
                                    row.key === draft.key
                                      ? { ...row, sender_ref: event.target.value }
                                      : row
                                  )
                                )
                              }
                            />
                            {submitted && (!draft.company_id || !draft.sender_ref) && (
                              <span className="service-email-field-error">
                                Empresa y emisor son obligatorios.
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {!isEditing && (
              <div className="service-email-mode-tabs" role="tablist" aria-label="Formato de carga">
                <button
                  type="button"
                  role="tab"
                  aria-selected={inputMode === "form"}
                  className={`service-email-mode-tab${inputMode === "form" ? " is-active" : ""}`}
                  onClick={() => {
                    setInputMode("form");
                    setJsonError(null);
                    setSubmitted(false);
                  }}
                >
                  Formulario
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={inputMode === "json"}
                  className={`service-email-mode-tab${inputMode === "json" ? " is-active" : ""}`}
                  onClick={() => {
                    setInputMode("json");
                    setJsonError(null);
                    setSubmitted(false);
                  }}
                >
                  JSON
                </button>
              </div>
            )}

            <div className="services-body services-body--wide service-email-form-grid">
              {!(inputMode === "json" && jsonMultiDay) && (
                <>
                  <div className="service-email-field service-email-field--full">
                    <label htmlFor="service-email-company">Empresa</label>
                    <BaseSelect
                      label=""
                      name="company_filter"
                      value={companyFilter}
                      placeholder="Selecciona empresa para filtrar emisores"
                      searchable
                      options={companySelectOptions}
                      onChange={(event) => handleCompanyChange(event.target.value)}
                    />
                  </div>

                  <div className="service-email-field service-email-field--full">
                    <div className="service-email-field__head">
                      <label htmlFor="service-email-sender">Emisor</label>
                      <button
                        type="button"
                        className="service-email-field__add-btn"
                        onClick={() => openSenderQuickAdd()}
                        disabled={!companyFilter}
                        title={
                          companyFilter
                            ? "Crear autorizador, solicitante o ambos"
                            : "Selecciona una empresa primero"
                        }
                      >
                        <UserPlus size={16} strokeWidth={2.25} aria-hidden />
                        Nuevo
                      </button>
                    </div>
                    <BaseSelect
                      label=""
                      name="sender_ref"
                      value={form.sender_ref}
                      placeholder={
                        companyFilter
                          ? "Selecciona autorizador o solicitante por correo"
                          : "Selecciona una empresa primero"
                      }
                      searchable
                      preserveCase
                      disabled={senderDisabled}
                      options={senderSelectOptions}
                      onChange={(event) => updateField("sender_ref", event.target.value)}
                    />
                    {submitted && !form.sender_ref && inputMode !== "json" && (
                      <span className="service-email-field-error">Selecciona un emisor.</span>
                    )}
                  </div>
                </>
              )}

              {inputMode === "json" && !isEditing ? (
                <div className="service-email-field service-email-field--full">
                  <div className="service-email-json-toolbar">
                    <label htmlFor="service-email-json">JSON del correo y solicitudes</label>
                    <label className="service-email-multiday-toggle">
                      <input
                        type="checkbox"
                        checked={jsonMultiDay}
                        onChange={(event) => {
                          setJsonMultiDay(event.target.checked);
                          setJsonError(null);
                          if (event.target.checked && !jsonText.trim()) {
                            setJsonText(MULTI_DAY_JSON_PLACEHOLDER);
                          }
                        }}
                      />
                      <span>Multi-día / varios correos</span>
                    </label>
                  </div>
                  <textarea
                    id="service-email-json"
                    className="service-email-json-textarea"
                    value={jsonText}
                    onChange={(event) => {
                      setJsonText(event.target.value);
                      setJsonError(null);
                    }}
                    placeholder={jsonMultiDay ? MULTI_DAY_JSON_PLACEHOLDER : JSON_PLACEHOLDER}
                    rows={16}
                    spellCheck={false}
                  />
                  <p className="service-email-solicitudes-hint">
                    {jsonMultiDay ? (
                      <>
                        Acepta <code>{`{ emails: [...] }`}</code>, un arreglo de correos, o un solo{" "}
                        <code>{`{ email, solicitudes }`}</code> con días distintos (se separa un
                        correo por día). Luego verificarás todos los emisores.
                      </>
                    ) : (
                      <>
                        Formato: objeto con <code>email</code> y arreglo <code>solicitudes</code>. El
                        emisor se toma del selector de arriba (o de <code>sender_ref</code> si viene
                        en el JSON).
                      </>
                    )}
                  </p>
                  {(jsonError || (submitted && !jsonText.trim())) && (
                    <span className="service-email-field-error">
                      {jsonError || "Pega el JSON del correo."}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <div className="service-email-field service-email-field--full">
                    <label htmlFor="service-email-subject">Asunto</label>
                    <input
                      id="service-email-subject"
                      type="text"
                      value={form.subject}
                      onChange={(event) => updateField("subject", event.target.value)}
                      placeholder="Asunto del correo recibido"
                    />
                    {submitted && !form.subject.trim() && (
                      <span className="service-email-field-error">Ingresa el asunto.</span>
                    )}
                  </div>

                  <div className="service-email-field">
                    <label htmlFor="service-email-received">Fecha/hora recibido</label>
                    <input
                      id="service-email-received"
                      type="datetime-local"
                      value={form.received_at}
                      onChange={(event) => updateField("received_at", event.target.value)}
                    />
                  </div>

                  <div className="service-email-field">
                    <label htmlFor="service-email-multiple">Mas de una solicitud</label>
                    <select
                      id="service-email-multiple"
                      value={form.has_multiple_requests ? "true" : "false"}
                      onChange={(event) =>
                        updateField("has_multiple_requests", event.target.value === "true")
                      }
                    >
                      <option value="false">No</option>
                      <option value="true">Si</option>
                    </select>
                  </div>

                  <div className="service-email-field">
                    <label htmlFor="service-email-status">Estado</label>
                    <select
                      id="service-email-status"
                      value={form.status}
                      onChange={(event) =>
                        updateField("status", event.target.value as ServiceEmailStatus)
                      }
                    >
                      <option value="PENDIENTE">Pendiente</option>
                      <option value="AGREGADO">Agregado</option>
                    </select>
                  </div>

                  <div className="service-email-field service-email-field--full service-email-solicitudes-section">
                    <div className="service-email-solicitudes-header">
                      <div>
                        <label>Solicitudes</label>
                        {form.has_multiple_requests && (
                          <p className="service-email-solicitudes-hint">
                            Al agregar fila se repiten día, hora, origen y destino de la anterior.
                          </p>
                        )}
                      </div>
                      {form.has_multiple_requests && (
                        <div className="service-email-solicitudes-header-actions">
                          {solicitudes.length > 1 && (
                            <button
                              type="button"
                              className="btn-secondary btn-secondary--compact"
                              onClick={repeatTripFromFirst}
                            >
                              Repetir fila 1
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-secondary btn-secondary--compact"
                            onClick={addSolicitud}
                          >
                            + Agregar fila
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="service-email-solicitudes-table-wrap">
                      <table className="service-email-solicitudes-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Cant Pax</th>
                            <th>Día</th>
                            <th>Hora</th>
                            <th>Origen</th>
                            <th>Destino</th>
                            {form.has_multiple_requests && <th>Acciones</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {solicitudes.map((row, index) => (
                            <tr key={row.key}>
                              <td>{index + 1}</td>
                              <td>
                                <input
                                  className="service-email-table-input"
                                  type="number"
                                  min="1"
                                  value={row.passenger_count}
                                  onChange={(event) =>
                                    updateSolicitud(row.key, { passenger_count: event.target.value })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="service-email-table-input"
                                  type="date"
                                  value={row.request_date}
                                  onChange={(event) =>
                                    updateSolicitud(row.key, { request_date: event.target.value })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="service-email-table-input"
                                  type="time"
                                  value={row.request_time}
                                  onChange={(event) =>
                                    updateSolicitud(row.key, { request_time: event.target.value })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="service-email-table-input"
                                  type="text"
                                  value={row.origin_text}
                                  onChange={(event) =>
                                    updateSolicitud(row.key, { origin_text: event.target.value })
                                  }
                                  placeholder="Origen"
                                />
                              </td>
                              <td>
                                <input
                                  className="service-email-table-input"
                                  type="text"
                                  value={row.destination_text}
                                  onChange={(event) =>
                                    updateSolicitud(row.key, {
                                      destination_text: event.target.value,
                                    })
                                  }
                                  placeholder="Destino"
                                />
                              </td>
                              {form.has_multiple_requests && (
                                <td className="service-email-solicitudes-actions">
                                  {index > 0 && (
                                    <button
                                      type="button"
                                      className="service-email-solicitud-repeat"
                                      onClick={() => repeatTripFromPrevious(row.key, index)}
                                      title="Repetir día, hora, origen y destino de la fila anterior"
                                    >
                                      Repetir
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="service-email-solicitud-remove"
                                    onClick={() => removeSolicitud(row.key)}
                                    disabled={solicitudes.length === 1}
                                  >
                                    Quitar
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {submitted && !solicitudesValid && (
                      <span className="service-email-field-error">
                        Cada solicitud debe tener al menos 1 pasajero.
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        <footer className="services-footer">
          {wizardStep === "verify-senders" ? (
            <>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setWizardStep("edit");
                  setSubmitted(false);
                }}
                disabled={loading}
              >
                Volver al JSON
              </button>
              <button type="submit" className="btn-primary" disabled={loading || loadingVerifySenders}>
                {loading
                  ? "Creando..."
                  : `Crear ${verifyDrafts.length} correo${verifyDrafts.length !== 1 ? "s" : ""}`}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  loading ||
                  loadingSenders ||
                  (inputMode !== "json" && !companyFilter) ||
                  (inputMode === "json" && !jsonMultiDay && !companyFilter)
                }
              >
                {loading
                  ? "Guardando..."
                  : isEditing
                    ? "Guardar cambios"
                    : inputMode === "json" && jsonMultiDay
                      ? "Continuar a emisores"
                      : "Crear correo"}
              </button>
            </>
          )}
        </footer>
      </form>

      <EmailSenderQuickAddModal
        isOpen={senderModalOpen}
        onClose={() => {
          setSenderModalOpen(false);
          setSenderModalDraftKey(null);
        }}
        companyId={senderModalCompanyId}
        companyName={senderModalCompanyName}
        onCreated={handleSenderCreated}
      />
    </div>
  );
};

export default ServiceEmailRequestModal;
